import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardTitle, Col, Container, Row } from 'reactstrap'
import { FaImage, FaLockOpen, FaTrash, FaUsers } from 'react-icons/fa'
import { useAuth } from 'context/AuthContext'

type LastTimeWindow = 'day' | 'week' | 'month' | 'year';

const AdminHeader: React.FC = () => {
  const { token } = useAuth()
  const [usersCount, setUsersCount] = useState(0)
  const [newUsersCount, setNewUsersCount] = useState(0)
  const [newUsersPercentage, setNewUsersPercentage] = useState(0)
  const [screenshotsCount, setScreenshotsCount] = useState(0)
  const [screenshotsSize, setScreenshotsSize] = useState(0)
  const [newScreenshotsCount, setNewScreenshotsCount] = useState(0)
  const [newScreenshotsPercentage, setNewScreenshotsPercentage] = useState(0)
  const [publicScreenshotsCount, setPublicScreenshotsCount] = useState(0)
  const [newPublicScreenshotsCount, setNewPublicScreenshotsCount] = useState(0)
  const [newPublicScreenshotsPercentage, setNewPublicScreenshotsPercentage] = useState(0)
  const [deletedScreenshotsCount, setDeletedScreenshotsCount] = useState(0)
  const [newDeletedScreenshotsCount, setNewDeletedScreenshotsCount] = useState(0)
  const [newDeletedScreenshotsPercentage, setNewDeletedScreenshotsPercentage] = useState(0)
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<LastTimeWindow>('day')
	
  useEffect(() => {
    const fetchAdminHome = async () => {
      try {
        const response = await fetch('/api/admin/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ getLastTimeWindow: selectedTimeWindow }),
        })
				
        const data = await response.json()
        setUsersCount(data.users.total)
        setNewUsersCount(data.users.new)
        setNewUsersPercentage(data.users.newPercentage)
        setScreenshotsCount(data.screenshots.total)
        setNewScreenshotsCount(data.screenshots.new)
        setNewScreenshotsPercentage(data.screenshots.newPercentage)
        setPublicScreenshotsCount(data.publicScreenshots.total)
        setNewPublicScreenshotsCount(data.publicScreenshots.new)
        setNewPublicScreenshotsPercentage(data.publicScreenshots.newPercentage)
        setDeletedScreenshotsCount(data.deletedScreenshots.total)
        setNewDeletedScreenshotsCount(data.deletedScreenshots.new)
        setNewDeletedScreenshotsPercentage(data.deletedScreenshots.newPercentage)
        
        const sizeResponse = await fetch('/api/admin/screenshot/all/size', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        
        const sizeData = await sizeResponse.json()
        setScreenshotsSize(sizeData.totalSizeGb)
      } catch (err) {
        console.error('Failed to fetch admin home data', err)
      }
    }
		
    fetchAdminHome()
  }, [token, selectedTimeWindow]) // Ensure dependencies are correctly specified
	
  return (
    <>
      <div className="header bg-gradient-info pb-4 pt-5 pt-md-6">
        <Container fluid className="mt-4">
          <div className="header-body">
            <Row className="mb-4">
              <Col>
                <select
                  value={selectedTimeWindow}
                  onChange={(e) => setSelectedTimeWindow(e.target.value as LastTimeWindow)}
                  className="form-control w-auto"
                >
                  <option value="day">Last Day</option>
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="year">Last Year</option>
                </select>
              </Col>
            </Row>
            <Row>
              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
													Total users
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">{usersCount}</span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-blue text-white rounded-circle shadow">
                          <FaUsers />
                        </div>
                      </Col>
                    </Row>
                    <p className="mt-3 mb-0 text-muted text-sm">
                      <span className="text-success mr-2">
                        <i className="fa fa-arrow-up" /> {newUsersPercentage}% (+{newUsersCount})
                      </span>{' '}
                      <span className="text-nowrap">Since last {selectedTimeWindow}</span>
                    </p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
													Total Screens
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">{screenshotsCount}</span>{ ' ' }
                        <i>({screenshotsSize} GO)</i>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-warning text-white rounded-circle shadow">
                          <FaImage />
                        </div>
                      </Col>
                    </Row>
                    <p className="mt-3 mb-0 text-muted text-sm">
                      <span className="text-success mr-2">
                        <i className="fa fa-arrow-up" /> {newScreenshotsPercentage}% (+{newScreenshotsCount})
                      </span>{' '}
                      <span className="text-nowrap">Since last {selectedTimeWindow}</span>
                    </p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
													Public Screens
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">{publicScreenshotsCount}</span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-green text-white rounded-circle shadow">
                          <FaLockOpen />
                        </div>
                      </Col>
                    </Row>
                    <p className="mt-3 mb-0 text-muted text-sm">
                      <span className="text-success mr-2">
                        <i className="fa fa-arrow-up" /> {newPublicScreenshotsPercentage}% (+{newPublicScreenshotsCount})
                      </span>{' '}
                      <span className="text-nowrap">Since last {selectedTimeWindow}</span>
                    </p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
													Screens deleted
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">{deletedScreenshotsCount}</span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-danger text-white rounded-circle shadow">
                          <FaTrash />
                        </div>
                      </Col>
                    </Row>
                    <p className="mt-3 mb-0 text-muted text-sm">
                      <span className="text-success mr-2">
                        <i className="fa fa-arrow-up" /> {newDeletedScreenshotsPercentage}% (+{newDeletedScreenshotsCount})
                      </span>{' '}
                      <span className="text-nowrap">Since last {selectedTimeWindow}</span>
                    </p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  )
}

export default AdminHeader
